import React, { useEffect, useMemo, useRef, useState } from "react";
import { writeStoredSession } from "../utils/accountLinks";

const BRAND = "#015b9a";
const LIGHT = "#cceaff";

const POS_PREFERENCE_KEYS = {
  categoryLayout: "pos_category_layout",
  deliveryEnabled: "pos_delivery_enabled",
  deliveryCharge: "pos_delivery_charge",
  taxBreakupOnBill: "pos_show_tax_breakup_on_bill",
  deliveryCopyLogoEnabled: "pos_delivery_copy_logo_enabled",
  restaurantNameSize: "pos_restaurant_name_size",
  customerNameSize: "pos_customer_name_size",
  qrOrderAlertsEnabled: "qr_order_alerts_enabled",
  orderCreatedToneEnabled: "pos_order_created_tone_enabled",
};

const POS_CATEGORY_LAYOUTS = {
  vertical: "vertical",
  horizontal: "horizontal",
};

const normalizePrinterWidthPreference = (value) => {
  const raw = String(value || "").trim().toLowerCase();
  if (!raw) return null;
  if (
    raw === "3" ||
    raw === "3in" ||
    raw === "3inch" ||
    raw === "3-inch" ||
    raw === "80mm" ||
    raw === "76mm" ||
    raw === "72mm" ||
    raw === "80"
  ) {
    return "3in";
  }
  if (
    raw === "2" ||
    raw === "2in" ||
    raw === "2inch" ||
    raw === "2-inch" ||
    raw === "58mm" ||
    raw === "58"
  ) {
    return "2in";
  }
  return null;
};

const normalizeNameSizePreference = (value) => {
  const raw = String(value || "").trim().toLowerCase();
  if (raw === "large") return "large";
  if (raw === "small") return "small";
  return null;
};

const normalizeMoneyInput = (text) => {
  const cleaned = String(text || "").replace(/[^0-9.]/g, "");
  if (!cleaned) return "";
  const [whole, ...rest] = cleaned.split(".");
  const decimals = rest.join("").slice(0, 2);
  return decimals ? `${whole}.${decimals}` : whole;
};

const POSSettingsPage = () => {
  const layoutOptions = useMemo(
    () => [
      {
        id: POS_CATEGORY_LAYOUTS.horizontal,
        title: "Side Categories",
        description: "Categories appear as a side rail with items on the right.",
        icon: "☰",
      },
      {
        id: POS_CATEGORY_LAYOUTS.vertical,
        title: "Top Categories",
        description: "Categories appear on top with a compact grid below.",
        icon: "▦",
      },
    ],
    []
  );

  const [selectedLayout, setSelectedLayout] = useState(
    POS_CATEGORY_LAYOUTS.vertical
  );
  const [deliveryEnabled, setDeliveryEnabled] = useState(false);
  const [deliveryCharge, setDeliveryCharge] = useState("0");
  const [showTaxBreakup, setShowTaxBreakup] = useState(true);
  const [printerWidth, setPrinterWidth] = useState("2in");
  const [showDeliveryLogo, setShowDeliveryLogo] = useState(false);
  const [qrOrderAlertsEnabled, setQrOrderAlertsEnabled] = useState(true);
  const [orderCreatedToneEnabled, setOrderCreatedToneEnabled] = useState(true);
  const [restaurantNameSize, setRestaurantNameSize] = useState("small");
  const [customerNameSize, setCustomerNameSize] = useState("small");
  const [editOpen, setEditOpen] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);
  const [profileError, setProfileError] = useState("");
  const [photoPreview, setPhotoPreview] = useState("");
  const [photoFile, setPhotoFile] = useState(null);
  const [photoChanged, setPhotoChanged] = useState(false);
  const fileInputRef = useRef(null);

  const [pName, setPName] = useState("");
  const [pEmail, setPEmail] = useState("");
  const [pShopName, setPShopName] = useState("");
  const [pAddr1, setPAddr1] = useState("");
  const [pAddr2, setPAddr2] = useState("");
  const [pAddr3, setPAddr3] = useState("");
  const [pCf1, setPCf1] = useState("");
  const [pCf2, setPCf2] = useState("");
  const [pCf3, setPCf3] = useState("");
  const [pGst, setPGst] = useState("");
  const [pUpi, setPUpi] = useState("");
  const [pSound, setPSound] = useState(1);
  const [pVibration, setPVibration] = useState(1);
  const [pShowLogo, setPShowLogo] = useState(1);
  const [pShowQr, setPShowQr] = useState(1);
  const [pShowPhone, setPShowPhone] = useState(1);
  const [pShowThanks, setPShowThanks] = useState(1);

  useEffect(() => {
    const savedLayout = localStorage.getItem(POS_PREFERENCE_KEYS.categoryLayout);
    const savedDeliveryEnabled = localStorage.getItem(
      POS_PREFERENCE_KEYS.deliveryEnabled
    );
    const savedDeliveryCharge = localStorage.getItem(
      POS_PREFERENCE_KEYS.deliveryCharge
    );
    const savedShowTaxBreakup = localStorage.getItem(
      POS_PREFERENCE_KEYS.taxBreakupOnBill
    );
    const savedDeliveryLogo = localStorage.getItem(
      POS_PREFERENCE_KEYS.deliveryCopyLogoEnabled
    );
    const savedPrinterWidth = localStorage.getItem("printerWidth");
    const savedPrinterWidthLegacy = localStorage.getItem("printer_width");
    const savedRestaurantNameSize = localStorage.getItem(
      POS_PREFERENCE_KEYS.restaurantNameSize
    );
    const savedCustomerNameSize = localStorage.getItem(
      POS_PREFERENCE_KEYS.customerNameSize
    );
    const savedQrOrderAlertsEnabled = localStorage.getItem(
      POS_PREFERENCE_KEYS.qrOrderAlertsEnabled
    );
    const savedOrderCreatedToneEnabled = localStorage.getItem(
      POS_PREFERENCE_KEYS.orderCreatedToneEnabled
    );

    if (savedLayout) setSelectedLayout(savedLayout);
    if (savedDeliveryEnabled !== null)
      setDeliveryEnabled(savedDeliveryEnabled === "true");
    if (savedDeliveryCharge !== null) setDeliveryCharge(savedDeliveryCharge);
    if (savedShowTaxBreakup !== null)
      setShowTaxBreakup(savedShowTaxBreakup === "true");
    if (savedDeliveryLogo !== null)
      setShowDeliveryLogo(savedDeliveryLogo === "true");

    const normalizedWidth =
      normalizePrinterWidthPreference(savedPrinterWidth) ||
      normalizePrinterWidthPreference(savedPrinterWidthLegacy) ||
      "2in";
    setPrinterWidth(normalizedWidth);

    const normalizedRestaurantNameSize =
      normalizeNameSizePreference(savedRestaurantNameSize) || "small";
    const normalizedCustomerNameSize =
      normalizeNameSizePreference(savedCustomerNameSize) || "small";
    setRestaurantNameSize(normalizedRestaurantNameSize);
    setCustomerNameSize(normalizedCustomerNameSize);
    setQrOrderAlertsEnabled(
      savedQrOrderAlertsEnabled === null
        ? true
        : savedQrOrderAlertsEnabled === "true"
    );
    setOrderCreatedToneEnabled(
      savedOrderCreatedToneEnabled === null
        ? true
        : savedOrderCreatedToneEnabled === "true"
    );
  }, []);

  useEffect(() => {
    const stored = JSON.parse(localStorage.getItem("userData") || "{}");
    const u = stored?.data || stored || {};
    setPName(u?.name || "");
    setPEmail(u?.email || "");
    setPShopName(u?.restaurant_name || "");
    setPAddr1(u?.addressline1 || "");
    setPAddr2(u?.addressline2 || "");
    setPAddr3(u?.addressline3 || "");
    setPCf1(u?.customfield1 || "");
    setPCf2(u?.customfield2 || "");
    setPCf3(u?.customfield3 || "");
    setPGst(u?.gstno || "");
    setPUpi(u?.upiaddress || "");
    setPSound(typeof u?.issoundenable === "number" ? u.issoundenable : 1);
    setPVibration(typeof u?.isvibrationenable === "number" ? u.isvibrationenable : 1);
    setPShowLogo(typeof u?.showlogoonprinter === "number" ? u.showlogoonprinter : 1);
    setPShowQr(typeof u?.showqrcodeonprinter === "number" ? u.showqrcodeonprinter : 1);
    setPShowPhone(typeof u?.showphonenumberonprinter === "number" ? u.showphonenumberonprinter : 1);
    setPShowThanks(typeof u?.showthanksonbill === "number" ? u.showthanksonbill : 1);
    let avatarUrl = u?.avatar_url || u?.photo || u?.logo || u?.restaurant_logo || "";
    if (avatarUrl && !/^https?:\/\//i.test(avatarUrl)) {
      avatarUrl = `https://qr.billsarthi.com/public/${avatarUrl.replace(/^\/?/, "")}`;
    }
    setPhotoPreview(avatarUrl || "");
    setPhotoFile(null);
    setPhotoChanged(false);
  }, [editOpen]);

  const saveLayout = (layout) => {
    setSelectedLayout(layout);
    localStorage.setItem(POS_PREFERENCE_KEYS.categoryLayout, layout);
  };

  const handleDeliveryToggle = (value) => {
    setDeliveryEnabled(value);
    localStorage.setItem(
      POS_PREFERENCE_KEYS.deliveryEnabled,
      value ? "true" : "false"
    );
  };

  const handleTaxBreakupToggle = (value) => {
    setShowTaxBreakup(value);
    localStorage.setItem(
      POS_PREFERENCE_KEYS.taxBreakupOnBill,
      value ? "true" : "false"
    );
  };

  const handleDeliveryLogoToggle = (value) => {
    setShowDeliveryLogo(value);
    localStorage.setItem(
      POS_PREFERENCE_KEYS.deliveryCopyLogoEnabled,
      value ? "true" : "false"
    );
  };

  const handleQrOrderAlertsToggle = (value) => {
    setQrOrderAlertsEnabled(value);
    localStorage.setItem(
      POS_PREFERENCE_KEYS.qrOrderAlertsEnabled,
      value ? "true" : "false"
    );
    window.dispatchEvent(new Event("qr-alert-settings-changed"));
  };

  const handleOrderCreatedToneToggle = (value) => {
    setOrderCreatedToneEnabled(value);
    localStorage.setItem(
      POS_PREFERENCE_KEYS.orderCreatedToneEnabled,
      value ? "true" : "false"
    );
  };

  const handlePrinterWidthChange = (value) => {
    if (!value || value === printerWidth) return;
    setPrinterWidth(value);
    localStorage.setItem("printerWidth", value);
    localStorage.setItem("printer_width", value);
  };

  const handleRestaurantNameSizeChange = (value) => {
    if (!value || value === restaurantNameSize) return;
    setRestaurantNameSize(value);
    localStorage.setItem(POS_PREFERENCE_KEYS.restaurantNameSize, value);
  };

  const handleCustomerNameSizeChange = (value) => {
    if (!value || value === customerNameSize) return;
    setCustomerNameSize(value);
    localStorage.setItem(POS_PREFERENCE_KEYS.customerNameSize, value);
  };

  const handleDeliveryChargeChange = (text) => {
    const normalized = normalizeMoneyInput(text);
    setDeliveryCharge(normalized);
    localStorage.setItem(
      POS_PREFERENCE_KEYS.deliveryCharge,
      normalized || "0"
    );
  };

  const handleDeliveryChargeBlur = () => {
    const normalized = normalizeMoneyInput(deliveryCharge);
    const finalValue = normalized || "0";
    setDeliveryCharge(finalValue);
    localStorage.setItem(POS_PREFERENCE_KEYS.deliveryCharge, finalValue);
  };

  const cropImageToSquare = (file, size = 250) =>
    new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement("canvas");
        canvas.width = size;
        canvas.height = size;
        const ctx = canvas.getContext("2d");
        const side = Math.min(img.width, img.height);
        const sx = Math.max(0, (img.width - side) / 2);
        const sy = Math.max(0, (img.height - side) / 2);
        ctx.drawImage(img, sx, sy, side, side, 0, 0, size, size);
        canvas.toBlob(
          (blob) => {
            if (!blob) {
              reject(new Error("Image crop failed"));
              return;
            }
            const cropped = new File([blob], file.name || "avatar.jpg", {
              type: blob.type || file.type || "image/jpeg",
            });
            resolve(cropped);
          },
          "image/jpeg",
          0.9
        );
      };
      img.onerror = reject;
      img.src = URL.createObjectURL(file);
    });

  const onPickPhoto = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    try {
      const cropped = await cropImageToSquare(f, 250);
      setPhotoFile(cropped);
      setPhotoPreview(URL.createObjectURL(cropped));
      setPhotoChanged(true);
    } catch (err) {
      setProfileError(err?.message || "Unable to process image");
    }
  };

  const removePhoto = () => {
    setPhotoFile(null);
    setPhotoPreview("");
    setPhotoChanged(true);
  };

  const saveProfile = async () => {
    const stored = JSON.parse(localStorage.getItem("userData") || "{}");
    const uid = stored?.data?.id || stored?.id;
    if (!uid) {
      setProfileError("User not found in storage");
      return;
    }
    setSavingProfile(true);
    setProfileError("");
    try {
      const form = new FormData();
      form.append("restaurantid", String(uid));
      form.append("restaurant_name", pShopName ?? "");
      form.append("name", pName ?? "");
      form.append("email", pEmail ?? "");
      form.append("addressline1", pAddr1 ?? "");
      form.append("addressline2", pAddr2 ?? "");
      form.append("addressline3", pAddr3 ?? "");
      form.append("issoundenable", String(pSound ?? 0));
      form.append("isvibrationenable", String(pVibration ?? 0));
      form.append("customfield1", pCf1 ?? "");
      form.append("customfield2", pCf2 ?? "");
      form.append("customfield3", pCf3 ?? "");
      form.append("gstno", pGst ?? "");
      form.append("upiaddress", pUpi ?? "");
      form.append("showlogoonprinter", String(pShowLogo ?? 0));
      form.append("showqrcodeonprinter", String(pShowQr ?? 0));
      form.append("showphonenumberonprinter", String(pShowPhone ?? 0));
      form.append("showthanksonbill", String(pShowThanks ?? 0));
      if (photoChanged && photoFile instanceof File) {
        form.append("photo", photoFile, photoFile.name || "avatar.jpg");
      }

      const resp = await fetch(
        "https://qr.billsarthi.in/public/api/edit/profile",
        {
          method: "POST",
          body: form,
        }
      );
      const result = await resp.json().catch(() => ({}));
      if (!resp.ok || result?.success === false) {
        throw new Error(result?.message || `HTTP ${resp.status}`);
      }

      const prev = JSON.parse(localStorage.getItem("userData") || "{}");
      const prevData = prev?.data || {};
      const updatedUser = result?.data
        ? { ...prevData, ...(result.data || {}) }
        : {
            ...prevData,
            restaurant_name: pShopName,
            name: pName,
            email: pEmail,
            addressline1: pAddr1,
            addressline2: pAddr2,
            addressline3: pAddr3,
            issoundenable: pSound,
            isvibrationenable: pVibration,
            customfield1: pCf1,
            customfield2: pCf2,
            customfield3: pCf3,
            gstno: pGst,
            upiaddress: pUpi,
            showlogoonprinter: pShowLogo,
            showqrcodeonprinter: pShowQr,
            showphonenumberonprinter: pShowPhone,
            showthanksonbill: pShowThanks,
          };

      writeStoredSession(
        { ...(prev || {}), success: true, data: updatedUser },
        { preserveAccountLinkContext: true }
      );
      setEditOpen(false);
    } catch (err) {
      setProfileError(err?.message || "Error updating profile");
    } finally {
      setSavingProfile(false);
    }
  };

  return (
    <div style={sx.page}>
      <div style={sx.header}>
        <div>
          <div style={sx.title}>POS Settings</div>
          <div style={sx.subtitle}>Control POS layout and billing preferences</div>
        </div>
        <div style={sx.headerBadge}>⚙️</div>
      </div>

      <div style={sx.section}>
        <div style={sx.sectionTitle}>Category Layout</div>
        <div style={sx.sectionDesc}>
          Choose how categories appear on the order screen.
        </div>
        <div style={sx.optionList}>
          {layoutOptions.map((option) => {
            const isSelected = selectedLayout === option.id;
            return (
              <button
                key={option.id}
                style={{
                  ...sx.optionCard,
                  ...(isSelected ? sx.optionCardActive : null),
                }}
                onClick={() => saveLayout(option.id)}
              >
                <div
                  style={{
                    ...sx.optionIcon,
                    ...(isSelected ? sx.optionIconActive : null),
                  }}
                >
                  {option.icon}
                </div>
                <div style={sx.optionText}>
                  <div style={sx.optionTitle}>{option.title}</div>
                  <div style={sx.optionDesc}>{option.description}</div>
                </div>
                <div style={{ ...sx.radio, ...(isSelected ? sx.radioOn : sx.radioOff) }}>
                  {isSelected ? "✓" : ""}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      <div style={sx.section}>
        <div style={sx.sectionTitle}>Delivery Settings</div>
        <div style={sx.sectionDesc}>
          Enable delivery charges and set a default amount.
        </div>
        <div style={sx.rowBetween}>
          <div style={sx.rowLabel}>Enable Delivery</div>
          <label style={sx.toggle} className="pos-toggle">
            <input
              type="checkbox"
              checked={deliveryEnabled}
              onChange={(e) => handleDeliveryToggle(e.target.checked)}
            />
            <span className="pos-toggle-slider" />
          </label>
        </div>
        <div
          style={{
            ...sx.inputWrap,
            ...(deliveryEnabled ? null : sx.inputWrapDisabled),
          }}
        >
          <span style={sx.currency}>Rs</span>
          <input
            style={sx.input}
            placeholder="Default delivery charge"
            value={deliveryCharge}
            onChange={(e) => handleDeliveryChargeChange(e.target.value)}
            onBlur={handleDeliveryChargeBlur}
            disabled={!deliveryEnabled}
          />
        </div>
        <div style={sx.helperText}>
          This amount will be prefilled on the Order Screen.
        </div>
      </div>

      <div style={sx.section}>
        <div style={sx.sectionTitle}>Delivery Copy Logo</div>
        <div style={sx.sectionDesc}>
          Show delivery logo on delivery copy print.
        </div>
        <div style={sx.rowBetween}>
          <div style={sx.rowLabel}>Enable delivery logo</div>
          <label style={sx.toggle} className="pos-toggle">
            <input
              type="checkbox"
              checked={showDeliveryLogo}
              onChange={(e) => handleDeliveryLogoToggle(e.target.checked)}
            />
            <span className="pos-toggle-slider" />
          </label>
        </div>
      </div>

      <div style={sx.section}>
        <div style={sx.sectionTitle}>Tax Breakup on Bill</div>
        <div style={sx.sectionDesc}>
          Show GST breakup on printed bills.
        </div>
        <div style={sx.rowBetween}>
          <div style={sx.rowLabel}>Show tax breakup</div>
          <label style={sx.toggle} className="pos-toggle">
            <input
              type="checkbox"
              checked={showTaxBreakup}
              onChange={(e) => handleTaxBreakupToggle(e.target.checked)}
            />
            <span className="pos-toggle-slider" />
          </label>
        </div>
      </div>

      <div style={sx.section}>
        <div style={sx.sectionTitle}>QR Order Alerts</div>
        <div style={sx.sectionDesc}>
          Keep QR order bell, sound and popup alerts enabled.
        </div>
        <div style={sx.rowBetween}>
          <div style={sx.rowLabel}>Enable QR alerts</div>
          <label style={sx.toggle} className="pos-toggle">
            <input
              type="checkbox"
              checked={qrOrderAlertsEnabled}
              onChange={(e) => handleQrOrderAlertsToggle(e.target.checked)}
            />
            <span className="pos-toggle-slider" />
          </label>
        </div>
      </div>

      <div style={sx.section}>
        <div style={sx.sectionTitle}>Order Created Tone</div>
        <div style={sx.sectionDesc}>
          Play a short success tone when a new order is created from Order Screen.
        </div>
        <div style={sx.rowBetween}>
          <div style={sx.rowLabel}>Enable order success tone</div>
          <label style={sx.toggle} className="pos-toggle">
            <input
              type="checkbox"
              checked={orderCreatedToneEnabled}
              onChange={(e) => handleOrderCreatedToneToggle(e.target.checked)}
            />
            <span className="pos-toggle-slider" />
          </label>
        </div>
      </div>

      <div style={sx.section}>
        <div style={sx.sectionTitle}>Printer Width</div>
        <div style={sx.sectionDesc}>
          Choose a printer size for receipts.
        </div>
        <div style={sx.optionRow}>
          {[
            { key: "2in", label: "2 inch (58mm)" },
            { key: "3in", label: "3 inch (80mm)" },
          ].map((option) => {
            const active = printerWidth === option.key;
            return (
              <button
                key={option.key}
                style={{
                  ...sx.chipBtn,
                  ...(active ? sx.chipBtnActive : null),
                }}
                onClick={() => handlePrinterWidthChange(option.key)}
              >
                {option.label}
              </button>
            );
          })}
        </div>
      </div>

      <div style={sx.section}>
        <div style={sx.sectionTitle}>Bill Name Size</div>
        <div style={sx.sectionDesc}>
          Control font size for restaurant and customer names.
        </div>
        <div style={sx.subSectionLabel}>Restaurant name</div>
        <div style={sx.optionRow}>
          {[
            { key: "small", label: "Small" },
            { key: "large", label: "Large" },
          ].map((option) => {
            const active = restaurantNameSize === option.key;
            return (
              <button
                key={option.key}
                style={{
                  ...sx.chipBtn,
                  ...(active ? sx.chipBtnActive : null),
                }}
                onClick={() => handleRestaurantNameSizeChange(option.key)}
              >
                {option.label}
              </button>
            );
          })}
        </div>
        <div style={sx.subSectionLabel}>Customer name</div>
        <div style={sx.optionRow}>
          {[
            { key: "small", label: "Small" },
            { key: "large", label: "Large" },
          ].map((option) => {
            const active = customerNameSize === option.key;
            return (
              <button
                key={option.key}
                style={{
                  ...sx.chipBtn,
                  ...(active ? sx.chipBtnActive : null),
                }}
                onClick={() => handleCustomerNameSizeChange(option.key)}
              >
                {option.label}
              </button>
            );
          })}
        </div>
      </div>

      <div style={sx.section}>
        <div style={sx.sectionTitle}>Restaurant Profile</div>
        <div style={sx.sectionDesc}>
          Edit restaurant details, printer toggles and logo.
        </div>
        <button style={sx.primaryBtn} onClick={() => setEditOpen(true)}>
          Edit Profile
        </button>
      </div>

      {editOpen ? (
        <div style={sx.modalBackdrop} onClick={() => setEditOpen(false)}>
          <div style={sx.modalCard} onClick={(e) => e.stopPropagation()}>
            <div style={sx.modalHeader}>
              <div style={sx.modalTitle}>Edit Profile</div>
              <button style={sx.modalClose} onClick={() => setEditOpen(false)}>
                ✕
              </button>
            </div>
            {profileError ? <div style={sx.modalError}>{profileError}</div> : null}

            <div style={sx.modalGrid}>
              <div style={sx.modalField}>
                <label style={sx.modalLabel}>Restaurant Name</label>
                <input style={sx.modalInput} value={pShopName} onChange={(e) => setPShopName(e.target.value)} />
              </div>
              <div style={sx.modalField}>
                <label style={sx.modalLabel}>Owner Name</label>
                <input style={sx.modalInput} value={pName} onChange={(e) => setPName(e.target.value)} />
              </div>
              <div style={sx.modalField}>
                <label style={sx.modalLabel}>Email</label>
                <input style={sx.modalInput} value={pEmail} onChange={(e) => setPEmail(e.target.value)} />
              </div>
              <div style={sx.modalField}>
                <label style={sx.modalLabel}>Address Line 1</label>
                <input style={sx.modalInput} value={pAddr1} onChange={(e) => setPAddr1(e.target.value)} />
              </div>
              <div style={sx.modalField}>
                <label style={sx.modalLabel}>Address Line 2</label>
                <input style={sx.modalInput} value={pAddr2} onChange={(e) => setPAddr2(e.target.value)} />
              </div>
              <div style={sx.modalField}>
                <label style={sx.modalLabel}>Address Line 3</label>
                <input style={sx.modalInput} value={pAddr3} onChange={(e) => setPAddr3(e.target.value)} />
              </div>
              <div style={sx.modalField}>
                <label style={sx.modalLabel}>Custom Field 1</label>
                <input style={sx.modalInput} value={pCf1} onChange={(e) => setPCf1(e.target.value)} />
              </div>
              <div style={sx.modalField}>
                <label style={sx.modalLabel}>Custom Field 2</label>
                <input style={sx.modalInput} value={pCf2} onChange={(e) => setPCf2(e.target.value)} />
              </div>
              <div style={sx.modalField}>
                <label style={sx.modalLabel}>Custom Field 3</label>
                <input style={sx.modalInput} value={pCf3} onChange={(e) => setPCf3(e.target.value)} />
              </div>
              <div style={sx.modalField}>
                <label style={sx.modalLabel}>GST No.</label>
                <input style={sx.modalInput} value={pGst} onChange={(e) => setPGst(e.target.value)} />
              </div>
              <div style={sx.modalField}>
                <label style={sx.modalLabel}>UPI Address</label>
                <input style={sx.modalInput} value={pUpi} onChange={(e) => setPUpi(e.target.value)} />
              </div>
            </div>

            <div style={sx.photoRow}>
              <div>
                <div style={sx.modalLabel}>Logo</div>
                {photoPreview ? (
                  <img src={photoPreview} alt="logo" style={sx.photoPreview} />
                ) : (
                  <div style={sx.photoPlaceholder}>No logo</div>
                )}
              </div>
              <div style={sx.photoActions}>
                <button style={sx.secondaryBtn} onClick={() => fileInputRef.current?.click()}>
                  Upload & Crop
                </button>
                <button style={sx.ghostBtn} onClick={removePhoto}>
                  Remove
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  style={{ display: "none" }}
                  onChange={onPickPhoto}
                />
              </div>
            </div>

            <div style={sx.toggleGrid}>
              <label style={sx.toggleRow}>
                <input type="checkbox" checked={!!pSound} onChange={(e) => setPSound(e.target.checked ? 1 : 0)} />
                Sound
              </label>
              <label style={sx.toggleRow}>
                <input type="checkbox" checked={!!pVibration} onChange={(e) => setPVibration(e.target.checked ? 1 : 0)} />
                Vibration
              </label>
              <label style={sx.toggleRow}>
                <input type="checkbox" checked={!!pShowLogo} onChange={(e) => setPShowLogo(e.target.checked ? 1 : 0)} />
                Show Logo on Bill
              </label>
              <label style={sx.toggleRow}>
                <input type="checkbox" checked={!!pShowQr} onChange={(e) => setPShowQr(e.target.checked ? 1 : 0)} />
                Show QR on Bill
              </label>
              <label style={sx.toggleRow}>
                <input type="checkbox" checked={!!pShowPhone} onChange={(e) => setPShowPhone(e.target.checked ? 1 : 0)} />
                Show Phone on Bill
              </label>
              <label style={sx.toggleRow}>
                <input type="checkbox" checked={!!pShowThanks} onChange={(e) => setPShowThanks(e.target.checked ? 1 : 0)} />
                Show Thanks on Bill
              </label>
            </div>

            <div style={sx.modalActions}>
              <button style={sx.ghostBtn} onClick={() => setEditOpen(false)}>
                Cancel
              </button>
              <button style={sx.primaryBtn} onClick={saveProfile} disabled={savingProfile}>
                {savingProfile ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

const sx = {
  page: {
    minHeight: "100vh",
    background: "#f6f8fb",
    padding: 16,
    fontFamily:
      "Inter, system-ui, -apple-system, Segoe UI, Roboto, 'Helvetica Neue', Arial",
    color: "#0f172a",
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    background: "linear-gradient(135deg, #015b9a, #003a66)",
    color: "#fff",
    padding: "14px 18px",
    borderRadius: 16,
    boxShadow: "0 10px 24px rgba(1,91,154,.18)",
    marginBottom: 16,
  },
  title: { fontSize: 18, fontWeight: 800 },
  subtitle: { fontSize: 12, opacity: 0.9, marginTop: 4 },
  headerBadge: {
    width: 40,
    height: 40,
    borderRadius: 12,
    background: "rgba(255,255,255,0.2)",
    display: "grid",
    placeItems: "center",
    fontWeight: 800,
  },
  section: {
    background: "#fff",
    padding: 16,
    borderRadius: 12,
    border: "1px solid #e5e7eb",
    marginBottom: 14,
    boxShadow: "0 6px 20px rgba(15,23,42,.05)",
  },
  sectionTitle: { fontSize: 16, fontWeight: 800, marginBottom: 6 },
  sectionDesc: { color: "#64748b", marginBottom: 12, fontSize: 13 },
  optionList: { display: "grid", gap: 12 },
  optionCard: {
    display: "flex",
    alignItems: "center",
    gap: 14,
    padding: 14,
    borderRadius: 12,
    border: "2px solid #e2e8f0",
    background: "#f8fafc",
    textAlign: "left",
    cursor: "pointer",
  },
  optionCardActive: {
    borderColor: BRAND,
    background: "#eef6ff",
  },
  optionIcon: {
    width: 48,
    height: 48,
    borderRadius: 24,
    background: LIGHT,
    display: "grid",
    placeItems: "center",
    fontSize: 20,
    color: BRAND,
  },
  optionIconActive: {
    background: BRAND,
    color: "#fff",
  },
  optionText: { flex: 1 },
  optionTitle: { fontWeight: 700, marginBottom: 4 },
  optionDesc: { fontSize: 12, color: "#64748b" },
  radio: {
    width: 24,
    height: 24,
    borderRadius: 12,
    border: "2px solid #cbd5e1",
    display: "grid",
    placeItems: "center",
    fontWeight: 900,
  },
  radioOn: { background: BRAND, color: "#fff", borderColor: BRAND },
  radioOff: { background: "#fff", color: "transparent" },

  rowBetween: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    marginBottom: 12,
  },
  rowLabel: { fontWeight: 700 },

  inputWrap: {
    display: "flex",
    alignItems: "center",
    border: "1px solid #e2e8f0",
    borderRadius: 10,
    padding: "8px 12px",
    background: "#fff",
  },
  inputWrapDisabled: { opacity: 0.6 },
  currency: { fontWeight: 700, color: BRAND, marginRight: 6 },
  input: {
    border: "none",
    outline: "none",
    flex: 1,
    fontSize: 14,
    color: "#0f172a",
    background: "transparent",
  },
  helperText: { fontSize: 12, color: "#64748b", marginTop: 8 },

  optionRow: { display: "flex", gap: 12, flexWrap: "wrap" },
  chipBtn: {
    padding: "10px 14px",
    borderRadius: 12,
    border: "2px solid #e2e8f0",
    background: "#f8fafc",
    fontWeight: 700,
    cursor: "pointer",
  },
  chipBtnActive: {
    borderColor: BRAND,
    background: "#eef6ff",
    color: BRAND,
  },
  subSectionLabel: {
    fontSize: 13,
    fontWeight: 700,
    marginTop: 8,
    marginBottom: 6,
  },

  toggle: {
    position: "relative",
    width: 42,
    height: 24,
    display: "inline-flex",
    alignItems: "center",
  },
  primaryBtn: {
    padding: "10px 14px",
    borderRadius: 10,
    border: "none",
    background: BRAND,
    color: "#fff",
    fontWeight: 700,
    cursor: "pointer",
  },
  secondaryBtn: {
    padding: "9px 12px",
    borderRadius: 10,
    border: `1px solid ${BRAND}`,
    background: "#eef6ff",
    color: BRAND,
    fontWeight: 700,
    cursor: "pointer",
  },
  ghostBtn: {
    padding: "9px 12px",
    borderRadius: 10,
    border: "1px solid #e2e8f0",
    background: "#fff",
    color: "#0f172a",
    fontWeight: 700,
    cursor: "pointer",
  },
  modalBackdrop: {
    position: "fixed",
    inset: 0,
    background: "rgba(15,23,42,0.45)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 16,
    zIndex: 999,
  },
  modalCard: {
    width: "min(900px, 100%)",
    background: "#fff",
    borderRadius: 16,
    padding: 16,
    boxShadow: "0 24px 40px rgba(15,23,42,.25)",
  },
  modalHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 12,
  },
  modalTitle: { fontWeight: 800, fontSize: 16 },
  modalClose: {
    border: "none",
    background: "transparent",
    fontSize: 18,
    cursor: "pointer",
  },
  modalError: {
    color: "#b91c1c",
    fontWeight: 700,
    marginBottom: 8,
  },
  modalGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
    gap: 10,
    marginBottom: 12,
  },
  modalField: { display: "grid", gap: 6 },
  modalLabel: { fontSize: 12, color: "#6b7280", fontWeight: 700 },
  modalInput: {
    padding: "8px 10px",
    borderRadius: 10,
    border: "1px solid #e2e8f0",
    background: "#f8fafc",
  },
  photoRow: {
    display: "flex",
    gap: 12,
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    marginBottom: 12,
  },
  photoPreview: {
    width: 90,
    height: 90,
    objectFit: "cover",
    borderRadius: 12,
    border: "1px solid #e2e8f0",
  },
  photoPlaceholder: {
    width: 90,
    height: 90,
    borderRadius: 12,
    border: "1px dashed #e2e8f0",
    display: "grid",
    placeItems: "center",
    color: "#94a3b8",
    fontSize: 12,
  },
  photoActions: { display: "flex", gap: 8, alignItems: "center" },
  toggleGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
    gap: 8,
    marginBottom: 12,
  },
  toggleRow: { display: "flex", alignItems: "center", gap: 8, fontWeight: 600 },
  modalActions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: 8,
  },
};

const styles = document.createElement("style");
styles.innerHTML = `
  .pos-toggle input {
    opacity: 0;
    width: 0;
    height: 0;
  }
  .pos-toggle-slider {
    position: absolute;
    inset: 0;
    background: #cbd5e1;
    border-radius: 999px;
    transition: background 0.2s ease;
  }
  .pos-toggle-slider::before {
    content: "";
    position: absolute;
    height: 18px;
    width: 18px;
    left: 3px;
    top: 3px;
    background: #f1f5f9;
    border-radius: 50%;
    transition: transform 0.2s ease, background 0.2s ease;
    box-shadow: 0 2px 6px rgba(15, 23, 42, 0.25);
  }
  .pos-toggle input:checked + .pos-toggle-slider {
    background: #cceaff;
  }
  .pos-toggle input:checked + .pos-toggle-slider::before {
    transform: translateX(18px);
    background: #015b9a;
  }
`;
if (!document.getElementById("pos-settings-style")) {
  styles.id = "pos-settings-style";
  document.head.appendChild(styles);
}

export default POSSettingsPage;
